// /src/components/modals/PreviewVitrinePremiumModal.jsx
// VERSÃO 11.0 SUPREMA - CINEMATIC PREVIEW & THEME SYNC

import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    FiX, FiGrid, FiLayout, FiMapPin, FiShield, FiMaximize2, 
    FiZap, FiChevronLeft, FiChevronRight, FiCamera, FiHome, 
    FiStar, FiDollarSign, FiArrowRight 
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const resolveImagePath = (path) => {
    if (!path) return 'https://via.placeholder.com/1200x800?text=MOS+GOLD+ASSET';
    if (path.startsWith('http')) return path;
    const formattedPath = path.startsWith('/') ? path : `/${path}`;
    return `https://mos-imoveis.masterofsites.com.br${formattedPath}`;
};

const fmt = (val) => new Intl.NumberFormat('pt-BR', { 
    style: 'currency', currency: 'BRL', maximumFractionDigits: 0 
}).format(val || 0);

export default function PreviewVitrinePremiumModal({ imovel, onClose }) {
    const [activeCategory, setActiveCategory] = useState('todos');
    const [activeIdx, setActiveIdx] = useState(0);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const navigate = useNavigate();

    const tipo = imovel.tipoNegocio || 'Venda';
    const vVenda = imovel.valores?.venda || imovel.valorVenda || 0;
    const vAluguel = imovel.valores?.aluguel || imovel.valorAluguel || 0;

    const categorias = useMemo(() => ({
        todos: [...(imovel.imagensDestaque || []), ...(imovel.imagensQuartos || []), ...(imovel.imagensBanheiros || []), ...(imovel.imagensOutros || [])],
        fachada: imovel.imagensDestaque || [],
        quartos: imovel.imagensQuartos || [],
        banheiros: imovel.imagensBanheiros || [],
        social: imovel.imagensOutros || []
    }), [imovel]);

    const currentGallery = useMemo(() => {
        const galeria = categorias[activeCategory] || [];
        return galeria.length > 0 ? galeria : categorias.todos;
    }, [categorias, activeCategory]);

    useEffect(() => { setActiveIdx(0); }, [activeCategory]);

    const nextImg = (e) => { e?.stopPropagation(); setActiveIdx((prev) => (prev + 1) % currentGallery.length); };
    const prevImg = (e) => { e?.stopPropagation(); setActiveIdx((prev) => (prev === 0 ? currentGallery.length - 1 : prev - 1)); };

    if (!imovel) return null;

    return (
        <div className="fixed inset-0 z-[2000] flex flex-col items-center justify-center p-4 md:p-8 backdrop-blur-3xl bg-black/60 overflow-hidden">
            
            {/* 🛠️ CONTROLES SUPERIORES */}
            <div className="w-full max-w-[1600px] mb-8 flex justify-between items-center animate-in slide-in-from-top-4 duration-500">
                <div className="flex items-center gap-6">
                    <button onClick={onClose} className="p-4 rounded-2xl bg-black/40 border border-white/10 text-white hover:bg-red-600 hover:rotate-90 transition-all shadow-2xl">
                        <FiX size={24} />
                    </button>
                    
                    <div className="flex bg-black/40 backdrop-blur-md border border-white/10 p-1.5 rounded-2xl">
                        {[
                            { id: 'todos', label: 'Tudo', icon: FiGrid },
                            { id: 'fachada', label: 'Fachada', icon: FiStar },
                            { id: 'quartos', label: 'Suítes', icon: FiHome }
                        ].map(cat => (categorias[cat.id].length > 0 || cat.id === 'todos') && (
                            <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all
                                ${activeCategory === cat.id ? 'bg-accent text-black shadow-xl' : 'text-white/40 hover:text-white'}`}
                            >
                                <cat.icon size={14} /> {cat.label}
                            </button>
                        ))}
                    </div>
                </div>

                <button onClick={() => navigate(`/imovel/${imovel._id}`)} 
                        className="px-10 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.3em] bg-accent text-black shadow-2xl hover:scale-105 transition-all flex items-center gap-3">
                    Acessar Dossiê <FiArrowRight size={18} />
                </button>
            </div>

            {/* 📽️ PROJEÇÃO (MAIN STAGE) */}
            <motion.div layout className="w-full max-w-[1600px] h-[80vh] relative border border-white/10 rounded-[3.5rem] overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col lg:flex-row bg-[var(--bg-card)]">
                
                <div className="relative w-full lg:w-[70%] h-full group/media bg-black overflow-hidden">
                    <AnimatePresence mode="wait">
                        <motion.img 
                            key={activeIdx + activeCategory}
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            transition={{ duration: 0.6 }}
                            src={resolveImagePath(currentGallery[activeIdx])} 
                            className="w-full h-full object-cover opacity-90 transition-transform duration-[5s] group-hover/media:scale-110"
                        />
                    </AnimatePresence>
                    
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

                    <div className="absolute inset-0 flex justify-between items-center px-8 opacity-0 group-hover/media:opacity-100 transition-all">
                        <button onClick={prevImg} className="p-5 rounded-full bg-white/10 text-white hover:bg-accent hover:text-black transition-all shadow-2xl">
                            <FiChevronLeft size={32} />
                        </button>
                        <button onClick={nextImg} className="p-5 rounded-full bg-white/10 text-white hover:bg-accent hover:text-black transition-all shadow-2xl">
                            <FiChevronRight size={32} />
                        </button>
                    </div>

                    <div className="absolute bottom-10 left-10 flex items-center gap-4">
                        <span className="bg-black/60 backdrop-blur-xl px-5 py-2.5 rounded-xl border border-white/10 text-[10px] font-black text-white uppercase tracking-widest">
                           <FiCamera className="inline mr-3 text-accent" /> {activeIdx + 1} / {currentGallery.length}
                        </span>
                    </div>
                </div>

                {/* 📝 HUD DE DADOS DO ATIVO */}
                <div className="w-full lg:w-[30%] p-12 flex flex-col justify-between border-l border-white/5 relative bg-[var(--bg-card)]">
                    <div className="space-y-8 relative z-10">
                        <div className="space-y-4">
                            <span className="text-[9px] font-black uppercase tracking-[0.5em] text-accent flex items-center gap-2">
                                <FiMapPin size={12} /> {imovel.localizacao?.bairro || imovel.bairro}
                            </span>
                            <h2 className="text-3xl font-black uppercase tracking-tighter leading-[0.95] text-auto">
                                {imovel.titulo}
                            </h2>
                            <span className="inline-block px-3 py-1 rounded-lg bg-main-auto border border-auto text-[15px] font-black uppercase text-sec-auto opacity-50">
                                SKU: {imovel.codigoImovel}
                            </span>
                        </div>

                        <div className="pt-8 border-t border-auto space-y-6">
                            {tipo !== 'Aluguel' && (
                                <div>
                                    <span className="text-[8px] font-black text-accent uppercase tracking-widest opacity-60">Cotação Venda</span>
                                    <p className="text-4xl font-black tracking-tighter text-auto mt-1">{fmt(vVenda)}</p>
                                </div>
                            )}
                            
                            {(tipo === 'Aluguel' || tipo === 'Ambos') && vAluguel > 0 && (
                                <div className="p-6 rounded-2xl bg-accent/5 border border-accent/20">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FiZap size={14} className="text-accent animate-pulse" />
                                        <span className="text-[8px] font-black text-accent uppercase tracking-widest">Fluxo Mensal</span>
                                    </div>
                                    <p className="text-2xl font-black tracking-tighter text-auto">
                                        {fmt(vAluguel)} <span className="text-[10px] opacity-40">/MÊS</span>
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-6 pt-8 border-t border-auto">
                            <div>
                                <span className="text-[8px] font-black uppercase opacity-40 text-auto block mb-1">Área Útil</span>
                                <p className="text-xl font-black text-auto">{imovel.caracteristicas?.areaUtil || '--'} m²</p>
                            </div>
                            <div>
                                <span className="text-[8px] font-black uppercase opacity-40 text-auto block mb-1">Dormitórios</span>
                                <p className="text-xl font-black text-auto">{imovel.caracteristicas?.quartos || '0'} Unid</p>
                            </div>
                        </div>
                    </div>

                    <div className="pt-10">
                        <div className="flex items-center gap-4 p-5 rounded-[1.5rem] bg-[var(--bg-main)] border border-[var(--border-color)]">
                            <FiShield size={20} className="text-accent" />
                            <div className="flex flex-col">
                                <span className="text-[8px] font-black text-auto uppercase opacity-30">Verificação</span>
                                <span className="text-[10px] font-black text-accent uppercase tracking-widest">Certificado MOS GOLD</span>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* 🔥 VISION PROGRESS BAR */}
            <div className="w-full max-w-[1600px] h-1.5 mt-6 bg-white/5 rounded-full overflow-hidden">
                <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${((activeIdx + 1) / currentGallery.length) * 100}%` }}
                    className="h-full bg-accent shadow-[0_0_15px_rgba(var(--accent-rgb),0.5)]"
                />
            </div>
        </div>
    );
}